import { Box, Button, Flex, FormLabel, Input, Text } from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FileDropzone } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormSuccess } from '../../../components/FormSuccess'
import { MaskedInput } from '../../../components/MaskedInput'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { capitalizeName, EMAIL_REGEX, useAppToast } from '../../../helpers'
import { EntregaDeTrabalhoService } from '../../../services/EntregaDeTrabalho/EntregaDeTrabalhoService'
import {
  EntregaDeTrabalhoFormato,
  IEntregaDeTrabalhoFormPayload,
  IEntregaDeTrabalhoOption,
  IEntregaDeTrabalhoUpload
} from './models'
import {
  FORMAT_LABELS,
  MAX_FILES,
  MAX_FILE_SIZE_MB,
  MAX_TOTAL_SIZE_MB,
  RESPONSIBLE_LABELS,
  WORK_TYPE_LABELS
} from './submitConsts'

const PHONE_REGEX = /^\(\d{2}\) \d{4,5}-\d{4}$/

// Sobrevive a um reload no meio do upload: sem isso, o aluno reenviaria o formulário
// inteiro e duplicaria a entrega já criada no backend.
const PUBLIC_ID_STORAGE_KEY = 'entrega-de-trabalho:createdPublicId'

const emptyForm = {
  name: '',
  cpf: '',
  responsible: '' as '' | 'student' | 'other',
  responsableName: '',
  responsableCpf: '',
  email: '',
  emailConfirmation: '',
  phone: '',
  phoneConfirmation: '',
  format: '' as '' | EntregaDeTrabalhoFormato,
  courseId: '',
  teacherId: '',
  classDays: '',
  classHourStart: '',
  classHourEnds: '',
  type: '' as '' | 'pesquisa-de-campo' | 'trabalho-de-conclusão'
}

interface IFailedUpload {
  file: File
  reason: string
}

export default function EntregaDeTrabalhoSubmitPage() {
  const service = useMemo(() => new EntregaDeTrabalhoService(), [])
  const toast = useAppToast()

  const [teachers, setTeachers] = useState<IEntregaDeTrabalhoOption[]>([])
  const [coursesByFormat, setCoursesByFormat] = useState<Record<string, IEntregaDeTrabalhoOption[]>>({})
  const [form, setForm] = useState({ ...emptyForm })
  // Arquivos ainda pendentes de envio: começa com a seleção inicial e, após cada
  // tentativa, fica só com os que falharam (os que deram certo saem da lista).
  const [files, setFiles] = useState<File[]>([])
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  // Restaurado do sessionStorage no mount: se a aba recarregar depois da entrega já
  // ter sido criada, o formulário não é reenviado — só falta concluir os uploads.
  const [initialPublicId] = useState<string | null>(() => sessionStorage.getItem(PUBLIC_ID_STORAGE_KEY))
  const [createdPublicId, setCreatedPublicId] = useState<string | null>(initialPublicId)
  const [failedUploads, setFailedUploads] = useState<IFailedUpload[]>([])
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  // Arquivos que o backend confirma terem sido recebidos antes do reload — sem isso o
  // aluno veria a tela travada e vazia, sem saber se algo já tinha ido pra frente.
  const [resumedUploads, setResumedUploads] = useState<IEntregaDeTrabalhoUpload[]>([])
  const [resuming, setResuming] = useState(false)
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false)

  // Com a entrega já criada, os dados não podem mais ser alterados: só resta enviar os anexos.
  const isLocked = createdPublicId !== null
  const fieldsDisabled = submitting || isLocked

  const error = (key: string) => formErrors[key]?.[0]

  useEffect(() => {
    service
      .getFormTeachers()
      .then(setTeachers)
      .catch(() => toast.error('Erro ao carregar os professores'))
  }, [service, toast])

  // Confirma no backend o que já foi recebido antes do reload; se a entrega não existir
  // mais, destrava o formulário em vez de deixar o aluno preso a um public_id inválido.
  useEffect(() => {
    if (!initialPublicId) return

    setResuming(true)
    service
      .getByPublicId(initialPublicId)
      .then((entrega) => setResumedUploads(entrega.uploads))
      .catch(() => {
        sessionStorage.removeItem(PUBLIC_ID_STORAGE_KEY)
        setCreatedPublicId(null)
      })
      .finally(() => setResuming(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Evita disparar duas buscas para o mesmo formato quando o usuário troca rápido.
  const loadingFormatsRef = useRef(new Set<string>())

  const loadCourses = (format: EntregaDeTrabalhoFormato) => {
    if (coursesByFormat[format] || loadingFormatsRef.current.has(format)) return
    loadingFormatsRef.current.add(format)
    service
      .getFormCourses(format)
      .then((courses) => setCoursesByFormat((current) => ({ ...current, [format]: courses })))
      .catch(() => toast.error('Erro ao carregar os cursos'))
      .finally(() => loadingFormatsRef.current.delete(format))
  }

  const validate = (): Record<string, string[]> => {
    const errors: Record<string, string[]> = {}

    if (!form.name.trim()) errors.name = ['O nome do aluno é obrigatório.']
    if (form.cpf.length !== 14) errors.cpf = ['O CPF deve estar no formato 000.000.000-00.']
    if (!form.responsible) errors.responsible = ['Informe quem é o responsável pelo aluno.']
    if (form.responsible === 'other') {
      if (!form.responsableName.trim()) errors.responsable_name = ['O nome do responsável é obrigatório.']
      if (form.responsableCpf.length !== 14) {
        errors.responsable_cpf = ['O CPF do responsável deve estar no formato 000.000.000-00.']
      }
    }
    if (!form.email.trim()) errors.email = ['O e-mail é obrigatório.']
    else if (!EMAIL_REGEX.test(form.email.trim())) errors.email = ['Informe um e-mail válido.']
    else if (form.email.trim() !== form.emailConfirmation.trim()) {
      errors.email_confirmation = ['A confirmação do e-mail não confere.']
    }
    if (!PHONE_REGEX.test(form.phone)) errors.phone = ['O telefone deve estar no formato (xx) xxxxx-xxxx.']
    else if (form.phone !== form.phoneConfirmation) {
      errors.phone_confirmation = ['A confirmação do telefone não confere.']
    }
    if (!form.format) errors.format = ['O formato é obrigatório.']
    if (!form.courseId) errors.course_id = ['O curso é obrigatório.']
    if (!form.teacherId) errors.teacher_id = ['O professor é obrigatório.']
    if (!form.classDays.trim()) errors['class-days'] = ['Os dias das aulas são obrigatórios.']
    if (!form.classHourStart) errors['class-hour-start'] = ['O horário de início da aula é obrigatório.']
    if (!form.classHourEnds) errors['class-hour-ends'] = ['O horário de fim da aula é obrigatório.']
    if (!form.type) errors.type = ['O tipo de trabalho é obrigatório.']
    if (files.length === 0) errors.files = ['Envie ao menos um arquivo do trabalho.']

    return errors
  }

  const buildPayload = (): IEntregaDeTrabalhoFormPayload => ({
    name: form.name.trim(),
    cpf: form.cpf,
    responsible: form.responsible as 'student' | 'other',
    ...(form.responsible === 'other'
      ? { responsable_name: form.responsableName.trim(), responsable_cpf: form.responsableCpf }
      : {}),
    email: form.email.trim(),
    email_confirmation: form.emailConfirmation.trim(),
    phone: form.phone,
    phone_confirmation: form.phoneConfirmation,
    format: form.format as EntregaDeTrabalhoFormato,
    course_id: form.courseId,
    teacher_id: form.teacherId,
    'class-days': form.classDays.trim(),
    'class-hour-start': form.classHourStart,
    'class-hour-ends': form.classHourEnds,
    type: form.type as 'pesquisa-de-campo' | 'trabalho-de-conclusão'
  })

  /** Envia a fila de arquivos um a um e devolve os que falharam. */
  const sendFiles = async (publicId: string, queue: File[]): Promise<IFailedUpload[]> => {
    const failures: IFailedUpload[] = []
    setUploadProgress({ current: 0, total: queue.length })

    for (let index = 0; index < queue.length; index++) {
      const file = queue[index]
      try {
        await service.uploadPublicFile(publicId, file)
      } catch (err) {
        const error = err as AxiosError<{ errors?: Record<string, string[]>; message?: string }>
        failures.push({
          file,
          reason:
            error.response?.data?.errors?.file?.[0] ?? error.response?.data?.message ?? 'Falha no envio do arquivo.'
        })
      }
      setUploadProgress({ current: index + 1, total: queue.length })
    }

    return failures
  }

  const runUploads = async (publicId: string, queue: File[]) => {
    const failures = await sendFiles(publicId, queue)
    // Só ficam pendentes os que falharam; os que deram certo saem da lista.
    setFiles(failures.map((failure) => failure.file))
    setFailedUploads(failures)

    if (failures.length === 0) {
      sessionStorage.removeItem(PUBLIC_ID_STORAGE_KEY)
      setSubmitted(true)
      return
    }

    toast.error(
      'Alguns arquivos não foram enviados',
      'Seus dados já foram registrados. Tente enviar os arquivos que faltaram.'
    )
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setFormErrors({})

    const clientErrors = validate()
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors)
      toast.warning('Verifique os campos do formulário', Object.values(clientErrors)[0][0])
      return
    }

    setSubmitting(true)
    try {
      const publicId = await service.submitForm(buildPayload())
      if (!publicId) throw new Error('missing public id')

      setCreatedPublicId(publicId)
      sessionStorage.setItem(PUBLIC_ID_STORAGE_KEY, publicId)
      await runUploads(publicId, files)
    } catch (err) {
      // A API devolve 400 para erros de validação (ver Exceptions\Handler); 422 fica de reserva.
      const error = err as AxiosError<{ errors: Record<string, string[]>; message?: string }>
      const status = error.response?.status
      if ((status === 400 || status === 422) && error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
        toast.warning('Verifique os campos do formulário')
      } else {
        toast.error('Erro ao enviar o trabalho', err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleRetryUploads = async () => {
    if (!createdPublicId || files.length === 0) return

    setSubmitting(true)
    try {
      await runUploads(createdPublicId, files)
    } finally {
      setSubmitting(false)
    }
  }

  const handleFilesChange = (newFiles: File[]) => {
    setFiles(newFiles)
    setFailedUploads((prev) => prev.filter((failure) => newFiles.includes(failure.file)))
  }

  // Escape hatch para quem retomou após reload e não tem mais nada pendente: sem isso,
  // um aluno cujos arquivos já tinham sido confirmados ficaria preso na tela travada,
  // já que não há mais nenhum File novo pra justificar reenviar.
  // Não há como saber aqui se sobrou algum arquivo do envio original que não chegou a
  // ser confirmado — por isso a confirmação explícita, em vez de finalizar direto.
  const handleFinishWithoutNewFiles = () => {
    sessionStorage.removeItem(PUBLIC_ID_STORAGE_KEY)
    setSubmitted(true)
    setConfirmFinishOpen(false)
  }

  const resetForm = () => {
    setForm({ ...emptyForm })
    setFiles([])
    setFormErrors({})
    setCreatedPublicId(null)
    sessionStorage.removeItem(PUBLIC_ID_STORAGE_KEY)
    setFailedUploads([])
    setUploadProgress({ current: 0, total: 0 })
    setSubmitted(false)
  }

  const renderStudentFields = () => (
    <>
      <FormField label="Nome Completo do Aluno" error={error('name')}>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onBlur={(e) => setForm((prev) => ({ ...prev, name: capitalizeName(e.target.value) }))}
          disabled={fieldsDisabled}
        />
      </FormField>

      <FormField label="CPF do Aluno" mt={4} error={error('cpf')}>
        <MaskedInput
          mask="cpf"
          value={form.cpf}
          onChange={(cpf) => setForm({ ...form, cpf })}
          isDisabled={fieldsDisabled}
        />
      </FormField>

      <FormField label="Quem é o responsável pelo aluno?" mt={4} error={error('responsible')}>
        <FormSelect
          isDisabled={fieldsDisabled}
          value={form.responsible}
          onChange={(value) => setForm({ ...form, responsible: value as typeof form.responsible })}
          options={Object.entries(RESPONSIBLE_LABELS).map(([value, label]) => ({ value: value, label: label }))}
          placeholder="Selecione"
        />
      </FormField>

      {form.responsible === 'other' && (
        <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
          <Flex direction="column" flex={1}>
            <FormField label="Nome Completo do Responsável" mt={4} error={error('responsable_name')}>
              <Input
                value={form.responsableName}
                onChange={(e) => setForm({ ...form, responsableName: e.target.value })}
                onBlur={(e) => setForm((prev) => ({ ...prev, responsableName: capitalizeName(e.target.value) }))}
                disabled={fieldsDisabled}
              />
            </FormField>
          </Flex>
          <Flex direction="column" flex={1}>
            <FormField label="CPF do Responsável" mt={4} error={error('responsable_cpf')}>
              <MaskedInput
                mask="cpf"
                value={form.responsableCpf}
                onChange={(responsableCpf) => setForm({ ...form, responsableCpf })}
                isDisabled={fieldsDisabled}
              />
            </FormField>
          </Flex>
        </Flex>
      )}
    </>
  )

  const renderContactFields = () => (
    <>
      <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
        <Flex direction="column" flex={1}>
          <FormField label="E-mail" mt={4} error={error('email')}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              disabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
        <Flex direction="column" flex={1}>
          <FormField label="Confirme seu e-mail" mt={4} error={error('email_confirmation')}>
            <Input
              type="email"
              value={form.emailConfirmation}
              onChange={(e) => setForm({ ...form, emailConfirmation: e.target.value })}
              disabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
      </Flex>

      <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
        <Flex direction="column" flex={1}>
          <FormField label="Telefone" mt={4} error={error('phone')}>
            <MaskedInput
              mask="phone"
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              isDisabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
        <Flex direction="column" flex={1}>
          <FormField label="Confirme seu telefone" mt={4} error={error('phone_confirmation')}>
            <MaskedInput
              mask="phone"
              value={form.phoneConfirmation}
              onChange={(phoneConfirmation) => setForm({ ...form, phoneConfirmation })}
              isDisabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
      </Flex>
    </>
  )

  const renderCourseFields = () => (
    <>
      <FormField label="Formato" mt={4} error={error('format')}>
        <FormSelect
          isDisabled={fieldsDisabled}
          value={form.format}
          onChange={(value) => {
            const format = value as typeof form.format
            setForm({ ...form, format, courseId: '' })
            if (format) loadCourses(format)
          }}
          options={Object.entries(FORMAT_LABELS).map(([value, label]) => ({ value: value, label: label }))}
          placeholder="Selecione o formato"
        />
      </FormField>

      <FormField label="Curso" mt={4} error={error('course_id')}>
        <SearchableSelect
          placeholder={form.format ? 'Selecione o curso...' : 'Selecione o formato primeiro'}
          value={form.courseId}
          onChange={(value) => setForm({ ...form, courseId: value })}
          options={(form.format ? (coursesByFormat[form.format] ?? []) : []).map((course) => ({
            value: String(course.id),
            label: course.name
          }))}
          isDisabled={fieldsDisabled || !form.format}
        />
      </FormField>

      <FormField label="Professor" mt={4} error={error('teacher_id')}>
        <SearchableSelect
          placeholder="Selecione o professor..."
          value={form.teacherId}
          onChange={(value) => setForm({ ...form, teacherId: value })}
          options={teachers.map((teacher) => ({ value: String(teacher.id), label: teacher.name }))}
          isDisabled={fieldsDisabled}
        />
      </FormField>

      <FormField label="Dias das aulas" mt={4} error={error('class-days')}>
        <Input
          value={form.classDays}
          placeholder="Ex.: Segundas e Quartas"
          onChange={(e) => setForm({ ...form, classDays: e.target.value })}
          disabled={fieldsDisabled}
        />
      </FormField>

      <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
        <Flex direction="column" flex={1}>
          <FormField label="Início da aula" mt={4} error={error('class-hour-start')}>
            <Input
              type="time"
              value={form.classHourStart}
              onChange={(e) => setForm({ ...form, classHourStart: e.target.value })}
              disabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
        <Flex direction="column" flex={1}>
          <FormField label="Fim da aula" mt={4} error={error('class-hour-ends')}>
            <Input
              type="time"
              value={form.classHourEnds}
              onChange={(e) => setForm({ ...form, classHourEnds: e.target.value })}
              disabled={fieldsDisabled}
            />
          </FormField>
        </Flex>
      </Flex>

      <FormField label="Tipo de Trabalho" mt={4} error={error('type')}>
        <FormSelect
          isDisabled={fieldsDisabled}
          value={form.type}
          onChange={(value) => setForm({ ...form, type: value as typeof form.type })}
          options={Object.entries(WORK_TYPE_LABELS).map(([value, label]) => ({ value: value, label: label }))}
          placeholder="Selecione o tipo de trabalho"
        />
      </FormField>
    </>
  )

  const renderFilesSection = () => (
    <>
      <FormLabel mt={4}>Selecione arquivos (PDF, JPG, PNG, XLSX, PPTX, DOCX ou ZIP)</FormLabel>
      {isLocked && (
        <Text variant="caption" mb={2}>
          Seus dados já foram registrados. Envie os arquivos abaixo para concluir.
        </Text>
      )}
      {resuming && (
        <Text variant="caption" mb={2}>
          Verificando o que já foi enviado...
        </Text>
      )}
      {resumedUploads.length > 0 && (
        <Box mt={2} mb={4} p={3} border="1px solid" borderColor="green.300" borderRadius="md" bg="green.50">
          <Text fontWeight="bold" color="green.700">
            Estes arquivos já foram recebidos:
          </Text>
          {resumedUploads.map((upload) => (
            <Text key={upload.id} fontSize="sm" color="green.700" mt={1}>
              {upload.original_name}
            </Text>
          ))}
        </Box>
      )}
      <FileDropzone
        accept="workFile"
        files={files}
        onChange={handleFilesChange}
        maxFiles={MAX_FILES}
        maxSizeMB={MAX_FILE_SIZE_MB}
        maxTotalSizeMB={MAX_TOTAL_SIZE_MB}
        error={error('files')}
        isDisabled={submitting || resuming}
      />

      {submitting && uploadProgress.total > 0 && (
        <Text variant="caption" mt={3} textAlign="center">
          Enviando arquivo {uploadProgress.current} de {uploadProgress.total}...
        </Text>
      )}

      {failedUploads.length > 0 && (
        <Box mt={4} p={4} border="1px solid" borderColor="red.300" borderRadius="md" bg="red.50">
          <Text fontWeight="bold" color="red.600">
            Seus dados foram registrados, mas estes arquivos não foram enviados:
          </Text>
          {failedUploads.map((failure) => (
            <Text key={`${failure.file.name}-${failure.file.size}`} fontSize="sm" color="red.600" mt={1}>
              {failure.file.name} — {failure.reason}
            </Text>
          ))}
        </Box>
      )}
    </>
  )

  if (submitted) {
    return (
      <PublicFormLayout title="Entrega de Trabalho">
        <FormSuccess
          title="Trabalho enviado!"
          description="Recebemos o seu trabalho e os arquivos anexados. O professor fará a correção e você receberá o retorno por e-mail."
          actions={[{ label: 'Enviar outro trabalho', onClick: resetForm, variant: 'outline' }]}
        />
      </PublicFormLayout>
    )
  }

  return (
    <PublicFormLayout title="Entrega de Trabalho">
      <form onSubmit={handleSubmit}>
        {renderStudentFields()}
        {renderContactFields()}
        {renderCourseFields()}
        {renderFilesSection()}

        {isLocked ? (
          files.length === 0 && resumedUploads.length > 0 ? (
            <Button
              colorScheme="magenta"
              variant="outline"
              type="button"
              w="100%"
              size="lg"
              mt={6}
              isLoading={resuming}
              onClick={() => setConfirmFinishOpen(true)}
            >
              Já enviei todos os arquivos
            </Button>
          ) : (
            <Button
              colorScheme="magenta"
              type="button"
              w="100%"
              size="lg"
              mt={6}
              isLoading={submitting || resuming}
              isDisabled={files.length === 0}
              onClick={handleRetryUploads}
            >
              Tentar enviar novamente
            </Button>
          )
        ) : (
          <Button colorScheme="magenta" type="submit" w="100%" size="lg" mt={6} isLoading={submitting}>
            Enviar Trabalho
          </Button>
        )}
      </form>

      <ConfirmDialog
        isOpen={confirmFinishOpen}
        onClose={() => setConfirmFinishOpen(false)}
        onConfirm={handleFinishWithoutNewFiles}
        action="save"
        title="Finalizar entrega"
        description="Confirma que não tem mais nenhum arquivo pra enviar? Isso vai finalizar sua entrega."
        confirmLabel="Finalizar"
      />
    </PublicFormLayout>
  )
}
